import {useEffect, useMemo, useRef, useState} from 'react';
import {useUser} from '../../contexts/UserContext';
import {
  AUTO_DETECT_LANGUAGE_OPTION,
  LANGUAGE_OPTIONS,
  buildDialectOptions,
  normalizeTypeaheadText,
} from './createSessionFormUtils';

export default function useCreateSessionLanguageFields() {
  const {userData} = useUser();
  // Temporary: Auto-detect stays wired end-to-end but is only offered in the
  // language picker to superadmins while we dogfood. Remove this gate when
  // ready for general rollout.
  const canUseAutoDetect = userData?.role === 'superadmin';
  const [language, setLanguage] = useState('');
  const [dialect, setDialect] = useState('');
  const [languageSearch, setLanguageSearch] = useState('');
  const [isLanguageTypeaheadOpen, setIsLanguageTypeaheadOpen] = useState(false);
  const [dialectSearch, setDialectSearch] = useState('');
  const [isDialectTypeaheadOpen, setIsDialectTypeaheadOpen] = useState(false);
  const [shouldShowDialectTypeahead, setShouldShowDialectTypeahead] =
    useState(false);
  const languageTypeaheadRef = useRef<any>(null);
  const dialectTypeaheadRef = useRef<any>(null);

  useEffect(() => {
    const handlePointerDown = (event: any) => {
      const target = event.target;
      if (
        languageTypeaheadRef.current &&
        !languageTypeaheadRef.current.contains(target)
      ) {
        setIsLanguageTypeaheadOpen(false);
      }
      if (
        dialectTypeaheadRef.current &&
        !dialectTypeaheadRef.current.contains(target)
      ) {
        setIsDialectTypeaheadOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, []);

  const filteredLanguageOptions = useMemo(() => {
    const normalizedSearch = normalizeTypeaheadText(languageSearch);
    const candidates = normalizedSearch
      ? LANGUAGE_OPTIONS.filter(option =>
          option.searchText.includes(normalizedSearch),
        )
      : LANGUAGE_OPTIONS;

    // Auto-detect is pinned first (above Favorites) whenever it matches the
    // search; the section component renders it outside the favorite groups.
    // Superadmin-only until general rollout (see canUseAutoDetect above).
    const includeAutoDetect =
      canUseAutoDetect &&
      (!normalizedSearch ||
        AUTO_DETECT_LANGUAGE_OPTION.searchText.includes(normalizedSearch));

    return includeAutoDetect
      ? [AUTO_DETECT_LANGUAGE_OPTION, ...candidates]
      : candidates;
  }, [canUseAutoDetect, languageSearch]);

  const shouldShowLanguageSearchStatus =
    Boolean(languageSearch.trim()) && isLanguageTypeaheadOpen;
  const dialectOptions = useMemo(
    () => buildDialectOptions(language),
    [language],
  );
  const filteredDialectOptions = useMemo(() => {
    const normalizedSearch = normalizeTypeaheadText(dialectSearch);
    const candidates = normalizedSearch
      ? dialectOptions.filter(option =>
          option.searchText.includes(normalizedSearch),
        )
      : dialectOptions;

    return candidates;
  }, [dialectOptions, dialectSearch]);

  const hideDialectTypeahead = () => {
    setDialect('');
    setDialectSearch('');
    setIsDialectTypeaheadOpen(false);
    setShouldShowDialectTypeahead(false);
  };

  const resetLanguageFields = () => {
    setLanguage('');
    setDialect('');
    setLanguageSearch('');
    setIsLanguageTypeaheadOpen(false);
    setDialectSearch('');
    setIsDialectTypeaheadOpen(false);
    setShouldShowDialectTypeahead(false);
  };

  const handleLanguageFocus = () => {
    setIsLanguageTypeaheadOpen(true);
    hideDialectTypeahead();
  };

  const handleLanguageSearchChange = (event: any) => {
    const nextSearch = event.target.value;
    setLanguageSearch(nextSearch);
    setIsLanguageTypeaheadOpen(true);
    hideDialectTypeahead();

    if (nextSearch !== language) {
      setLanguage('');
    }
  };

  // Seed the language (and optional dialect) directly from an external
  // source such as a clicked Planner schedule card. We don't try to match
  // against LANGUAGE_OPTIONS so a patient's stored language string still
  // populates the field even if it isn't an exact supported-language key.
  const seedLanguageSelection = (languageName: any, dialectName = '') => {
    const lang = String(languageName || '').trim();
    if (!lang) {
      resetLanguageFields();
      return;
    }
    const dial = String(dialectName || '').trim();
    setLanguage(lang);
    setLanguageSearch(lang);
    setDialect(dial);
    setDialectSearch(dial);
    setIsLanguageTypeaheadOpen(false);
    setIsDialectTypeaheadOpen(false);
    setShouldShowDialectTypeahead(buildDialectOptions(lang).length > 0);
  };

  const handleSelectLanguageOption = (option: any) => {
    setLanguage(option.language);
    setDialect('');
    setLanguageSearch(option.label);
    setDialectSearch('');
    setIsLanguageTypeaheadOpen(false);
    setIsDialectTypeaheadOpen(false);
    setShouldShowDialectTypeahead(option.dialectCount > 0);
  };

  const handleClearLanguage = () => {
    setLanguage('');
    setDialect('');
    setLanguageSearch('');
    setDialectSearch('');
    setIsLanguageTypeaheadOpen(true);
    setIsDialectTypeaheadOpen(false);
    setShouldShowDialectTypeahead(false);
  };

  const handleDialectSearchChange = (event: any) => {
    const nextSearch = event.target.value;
    setDialectSearch(nextSearch);
    setIsDialectTypeaheadOpen(true);

    if (nextSearch !== dialect) {
      setDialect('');
    }
  };

  const handleSelectDialectOption = (option: any) => {
    setDialect(option.dialect);
    setDialectSearch(option.label);
    setIsDialectTypeaheadOpen(false);
  };

  const handleClearDialect = () => {
    setDialect('');
    setDialectSearch('');
    setIsDialectTypeaheadOpen(true);
  };

  return {
    dialect,
    dialectOptions,
    dialectSearch,
    dialectTypeaheadRef,
    filteredDialectOptions,
    filteredLanguageOptions,
    handleClearDialect,
    handleClearLanguage,
    handleDialectSearchChange,
    handleLanguageFocus,
    handleLanguageSearchChange,
    handleSelectDialectOption,
    handleSelectLanguageOption,
    isDialectTypeaheadOpen,
    isLanguageTypeaheadOpen,
    language,
    languageSearch,
    languageTypeaheadRef,
    resetLanguageFields,
    seedLanguageSelection,
    setIsDialectTypeaheadOpen,
    setIsLanguageTypeaheadOpen,
    shouldShowDialectTypeahead,
    shouldShowLanguageSearchStatus,
  };
}
